import { Box, Text, Link, styled } from '@ltvco/refresh-lib/theme';

import { CourtDocket } from '../../../types';
import { formatCourtDateWithTime } from '../../../utils';

import { IdbDataTable } from './IdbDataTable';

interface CourtCaseDetailsIdbDataProps {
  docketData: CourtDocket;
}

const DisclaimerBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

export const CourtCaseDetailsIdbData = ({
  docketData,
}: CourtCaseDetailsIdbDataProps) => {
  return (
    <Box sx={{ mt: 2 }}>
      <DisclaimerBox>
        <Text variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
          This information was originally retrieved from the{' '}
          <Link
            href="https://www.fjc.gov/research/idb/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Integrated Database (IDB)
          </Link>
          , a repository of federal civil, criminal, and bankruptcy cases
          maintained and hosted by the Federal Judicial Center (FJC). The FJC is
          the research and education agency of the judicial branch of the U.S.
          government. Under a working arrangement with the Administrative Office
          of the United States Courts (AOUSC), the FJC receives quarterly
          updates of the case-related data routinely reported by the courts to
          the AOUSC. The FJC then post-processes the data into a unified
          database, the IDB, which we share here. The FJC and the AOUSC were not
          actively involved in incorporating the data into Beenverified.
        </Text>
        <Text
          variant="body2"
          sx={{ lineHeight: 1.6, color: 'text.secondary', mt: 2 }}
        >
          Our sources make every effort to ensure this data is accurate, but it
          may be out of date and it may be matched to the wrong docket. Despite
          best efforts, due to gaps in this data, we can only match data to
          dockets via fuzzy automated systems that cannot be guaranteed.
        </Text>
        <Text
          variant="body2"
          sx={{ lineHeight: 1.6, color: 'text.secondary', mt: 2 }}
        >
          All fields and their descriptions are available in the code books on
          the{' '}
          <Link
            href="https://www.fjc.gov/research/idb/"
            target="_blank"
            rel="noopener noreferrer"
          >
            FJC Integrated Database website
          </Link>
          .
        </Text>
      </DisclaimerBox>
      <Box sx={{ mb: 2, textAlign: 'left' }}>
        <Text variant="h3" sx={{ color: 'text.secondary' }}>
          Values last updated{' '}
          {docketData.idb_data?.date_modified
            ? formatCourtDateWithTime(docketData.idb_data.date_modified)
            : 'Unknown'}
        </Text>
      </Box>
      <IdbDataTable docketData={docketData} />
    </Box>
  );
};
